/**
 * FunnelApp — the engine's state machine.
 *
 * Owns answers, the current step, navigation, event emission, outcome
 * resolution, and lead delivery. Renders the shell (logo + progress) around
 * either a StepRenderer, a value card, or the ResultPage.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "preact/hooks";
import type { Answers, FunnelConfig, Outcome } from "../types";
import type { FieldValue } from "../logic/validation";
import { validateStep } from "../logic/validation";
import { matchGroup } from "../logic/conditions";
import { RESULT, findStep, nextDestination } from "../logic/branching";
import { resolveOutcome } from "../logic/scoring";
import type { EventBus } from "../analytics/eventBus";
import {
  buildGhlFields,
  buildHumanAnswers,
  collectFbCookies,
  collectUrlParams,
  deliverLead,
  getLandingPageUrl,
  isTestMode,
  newSubmissionId,
  type LeadPayload,
} from "../crm/submit";
import { clearProgress, loadProgress, saveProgress } from "../state/progress";
import { resolveParams } from "../logic/dynamic";
import { ProgressContext } from "./progress-context";
import { DynamicContext } from "./dynamic-context";
import { Blocks } from "./Blocks";
import { StepRenderer } from "./StepRenderer";
import { ResultPage } from "./ResultPage";

type Phase = "step" | "value_card" | "result";

/**
 * Capture a step's non-PII single/multi-select ANSWER so the operator console can
 * compute per-question answer distributions. Emitted on step_complete via extra_json,
 * which the Worker stores RAW (unlike field_value, which it hashes for PII safety).
 * Only single_select/multi_select fields are captured — name/email/phone text fields
 * are NEVER put here. Shape: {"af": field_id, "av": value} (multi-select joined by "|").
 */
function buildStepAnswerExtra(
  fields: ReadonlyArray<{ id: string; type: string }>,
  answers: Answers,
): string | undefined {
  for (const f of fields) {
    if (f.type !== "single_select" && f.type !== "multi_select") continue;
    const v = answers[f.id];
    if (v === undefined || v === "") continue;
    return JSON.stringify({ af: f.id, av: Array.isArray(v) ? v.join("|") : String(v) });
  }
  return undefined;
}

export interface FunnelAppProps {
  config: FunnelConfig;
  sessionId: string;
  bus: EventBus;
}

function readInputValue(name: string): string | undefined {
  const el = document.querySelector<HTMLInputElement>(`input[name="${name}"]`);
  return el && el.value ? el.value : undefined;
}

export function FunnelApp({ config, sessionId, bus }: FunnelAppProps) {
  const firstStepId = config.steps[0]!.id;
  const submissionId = useRef(newSubmissionId());

  const [answers, setAnswers] = useState<Answers>({});
  const [stepId, setStepId] = useState<string>(firstStepId);
  const [phase, setPhase] = useState<Phase>("step");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [history, setHistory] = useState<string[]>([]);
  // Inline lead-data validation (LEAD_VALIDATION.md §6, Layer 1).
  const [remoteErrors, setRemoteErrors] = useState<Record<string, string>>({});
  const lastValidated = useRef<Record<string, string>>({});

  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const stepIdRef = useRef(stepId);
  stepIdRef.current = stepId;

  const step = findStep(config.steps, stepId) ?? config.steps[0]!;
  const stepIndex = config.steps.findIndex((s) => s.id === stepId);

  // Dynamic-element params: resolved once from the URL at mount, then frozen.
  const dynParams = useMemo(
    () => resolveParams(config.dynamic, window.location.search),
    [config],
  );

  // Real-time field validation hits a sibling route of the submit endpoint:
  // /api/submit → /api/validate.
  const validateUrl = config.lead_delivery.worker_url.replace(/\/[^/]*$/, "/validate");

  // ── lifecycle: resume, funnel_start, abandon ──────────────────
  useEffect(() => {
    const saved = loadProgress(config.funnel_id);
    if (saved && findStep(config.steps, saved.step_id)) {
      setAnswers(saved.answers);
      setStepId(saved.step_id);
      if (saved.history) setHistory(saved.history);
    }
    bus.emit("funnel_start");
    const onUnload = (): void => {
      if (phaseRef.current !== "result") {
        bus.emit("funnel_abandon", { step_id: stepIdRef.current });
      }
    };
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tracks whether the current stepId change came from a browser back gesture
  // (popstate event). When true, virtualPage replaces the current history entry
  // instead of pushing a new one — otherwise each browser back would create a
  // duplicate URL and the user would need to press back twice to advance.
  const fromPopstate = useRef(false);

  // ── step_view + virtual URL on entering a step ────────────────
  useEffect(() => {
    if (phase !== "step") return;
    bus.emit("step_view", { step_id: stepId, step_index: stepIndex });
    bus.virtualPage(stepId, fromPopstate.current);
    fromPopstate.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepId, phase]);

  // ── pixel checkpoints ─────────────────────────────────────────
  const fireStepPixels = useCallback(
    (completedStepId: string) => {
      for (const ev of config.analytics.pixel_events ?? []) {
        if (ev.at_step !== completedStepId) continue;
        if (ev.qualified_only || ev.disqualified_only) continue; // fired at result
        bus.pixel(ev.event, ev.value ? { value: ev.value, currency: ev.currency } : undefined);
      }
    },
    [config, bus],
  );

  const fireResultPixels = useCallback(
    (oc: Outcome) => {
      for (const ev of config.analytics.pixel_events ?? []) {
        const gated = ev.qualified_only || ev.disqualified_only;
        if (ev.at_step !== "result" && !gated) continue;
        if (ev.qualified_only && !oc.qualified) continue;
        if (ev.disqualified_only && oc.qualified) continue;
        // event_id matches the Worker's CAPI event_id format so Meta
        // deduplicates browser+CAPI Lead pairs (counts one event, not two).
        // The Pages Function uses our submissionId as the Worker's
        // correlation_id (see functions/api/submit.ts:162), so:
        //   browser eventID = `${submissionId}_lead`
        //   CAPI event_id   = `${correlation_id}_lead`  (= same string)
        const eventID = `${submissionId.current}_${ev.event.toLowerCase()}`;
        bus.pixel(
          ev.event,
          ev.value ? { value: ev.value, currency: ev.currency } : undefined,
          eventID,
        );
      }
    },
    [config, bus],
  );

  // ── lead delivery ─────────────────────────────────────────────
  // W1-C3 Lock 5 fix: return DeliveryResult so caller can branch on .ok
  // (fire funnel_submit only on confirmed server 200; form_submit_failure
  // on non-200). Previously this was Promise<void> + caller fired
  // funnel_submit unconditionally before await resolved.
  const submitLead = useCallback(
    async (
      useAnswers: Answers,
      oc: Outcome,
    ): Promise<{
      ok: boolean;
      status?: number;
      error?: string;
      data_invalid?: boolean;
      validation_errors?: { phone?: string; email?: string; zip?: string };
    }> => {
      const payload: LeadPayload = {
        funnel_id: config.funnel_id,
        session_id: sessionId,
        submission_id: submissionId.current,
        partial: false,
        ghl_fields: buildGhlFields(config, useAnswers),
        raw_answers: useAnswers,
        human_answers: buildHumanAnswers(config, useAnswers),
        outcome: oc,
        trustedform_cert: config.lead_delivery.trustedform
          ? readInputValue("xxTrustedFormCertUrl")
          : undefined,
        turnstile_token: readInputValue("cf-turnstile-response"),
        // url_params + page_url use the init-time snapshot from crm/submit.ts.
        // The SPA navigates from `/?fbclid=...` to `/quiz/<step_id>` after
        // first mount, stripping the query string. Using a live read here
        // would lose fbclid/utm_*/landing_url; the frozen snapshot preserves
        // them across the entire session.
        url_params: collectUrlParams(),
        page_url: getLandingPageUrl(),
        fb_cookies: collectFbCookies(),
        // Test mode: ?test=1 URL param at landing. Worker uses this to skip
        // notification fan-out + write is_test=1 + route CAPI to Meta Test
        // Events tab. The visible TEST MODE banner (rendered in JSX below)
        // makes it impossible for the operator to mistake a test for a real
        // submission.
        test_mode: isTestMode(),
      };
      if (config.lead_delivery.worker_url) {
        return await deliverLead(config.lead_delivery.worker_url, payload);
      }
      // No worker URL configured (dev/preview) → treat as success so we
      // still fire funnel_submit for analytics testing.
      return { ok: true };
    },
    [config, sessionId],
  );

  // ── advance / navigation ──────────────────────────────────────
  const advance = useCallback(
    (useAnswers: Answers) => {
      const answerExtra = buildStepAnswerExtra(step.fields, useAnswers);
      bus.emit("step_complete", answerExtra
        ? { step_id: stepId, step_index: stepIndex, extra_json: answerExtra }
        : { step_id: stepId, step_index: stepIndex });
      fireStepPixels(stepId);

      const dest = nextDestination(step, config.steps, useAnswers);
      if ((step.branching ?? []).length > 0) {
        bus.emit("branch_taken", { step_id: stepId, dest });
      }

      // Option A — early DQ exit: if all DQ-rule data is now answered AND
      // any DQ rule matched, skip the remaining steps (injury / contact)
      // and go straight to the soft-reject result page. No PII collected
      // (Q7-Q8 PII steps never run), no CAPI Lead fire, no InitiateCheckout
      // pixel. Glen 2026-05-25: contact info from DQ'd users is friction with
      // no purpose — they're not contactable under TCPA scope anyway.
      // Conservative gate: wait until ALL DQ rules can be evaluated (so
      // the soft-reject page can list every matched reason, not just the
      // first one). For this funnel that's after Q6 (other-driver-insurance).
      //
      // Phase 2 LD-1 Option D (2026-05-30): submitLead IS called on the
      // early-exit path so DQ analytics land in lead_ledger (status='dq',
      // NULL PII per LD-1 whitelist). Worker validates the PII whitelist
      // and 422s any DQ payload carrying PII. SPA UX unchanged — fire-and-
      // forget; user sees soft-reject result regardless of Worker outcome.
      if (dest !== RESULT && config.scoring.enabled) {
        const allDqFieldsAnswered = config.scoring.dq_rules.every((rule) =>
          rule.conditions.every((cond) => useAnswers[cond.field] !== undefined),
        );
        if (allDqFieldsAnswered) {
          const earlyOc = resolveOutcome(config, useAnswers);
          if (!earlyOc.qualified) {
            setOutcome(earlyOc);
            setPhase("result");
            bus.emit("outcome_view", { step_id: stepId, tier: earlyOc.tier_id });
            bus.emit("funnel_dq_early", {
              step_id: stepId,
              step_index: stepIndex,
              dq_count: earlyOc.dq_reasons.length,
            });
            fireResultPixels(earlyOc);
            clearProgress(config.funnel_id);
            // Phase 2 LD-1 Option D: fire submitLead with DQ payload (no PII —
            // steps 7-8 never ran). Pages Function submit.ts will detect
            // !outcome.qualified, build the LD-1-stripped DQ payload, and
            // POST to Worker /lead_capture which INSERTs status='dq'. Fire-
            // and-forget: any failure here doesn't affect UX (soft-reject
            // page already rendered). Errors are logged for observability.
            void submitLead(useAnswers, earlyOc).then((result) => {
              if (!result.ok) {
                bus.emit("form_submit_failure", {
                  step_id: stepId,
                  tier: earlyOc.tier_id,
                  submit_error_code: String(result.status ?? result.error ?? "dq_submit_failed"),
                });
              }
            });
            return;
          }
        }
      }

      if (dest === RESULT) {
        const oc = resolveOutcome(config, useAnswers);
        setOutcome(oc);
        setPhase("result");
        // W1-C2: outcome_view fires when result page renders.
        bus.emit("outcome_view", { step_id: stepId, tier: oc.tier_id });
        // W1-C2 + W1-C3 Lock 5 fix:
        // (1) form_submit_attempt fires immediately when user reaches the
        //     submit checkpoint (independent of whether the server accepts).
        // (2) funnel_submit fires ONLY after the Worker returns 200.
        //     form_submit_failure fires on non-200 / network failure.
        // (3) Pixels (Meta Lead, GA4 conversion) also fire only on confirmed
        //     submission per Lock 5 — prevents premature conversion counts
        //     when the server actually rejected.
        bus.emit("form_submit_attempt", { step_id: stepId, tier: oc.tier_id });
        clearProgress(config.funnel_id);
        const contactStepId = stepId;
        const submitStartMs = Date.now();
        void submitLead(useAnswers, oc).then((result) => {
          const submitLatencyMs = Date.now() - submitStartMs;
          if (result.ok) {
            bus.emit("funnel_submit", {
              step_id: stepId,
              tier: oc.tier_id,
              submit_latency_ms: submitLatencyMs,
              // Link the per-step journey to the lead row so SQL joins
              // (lead_ledger.correlation_id = funnel_events.lead_correlation_id)
              // resolve the full session walk without timestamp-matching.
              lead_correlation_id: submissionId.current,
            });
            fireResultPixels(oc);
            return;
          }
          // Layer 2 silent-drop recovery: Worker accepted with data_invalid:true
          // (Layer 1 was rate-limited or DNS inconclusive, then Layer 2 caught
          // the bad data at submit time). Without this branch the user would
          // see the success page even though their lead was never delivered to
          // a buyer. Route them back to the contact step with inline errors so
          // they can fix and resubmit.
          if (result.data_invalid) {
            bus.emit("form_submit_failure", {
              step_id: stepId,
              tier: oc.tier_id,
              submit_latency_ms: submitLatencyMs,
              submit_error_code: "data_invalid",
            });
            const errs = result.validation_errors ?? {};
            const remoteFieldErrors: Record<string, string> = {};
            if (errs.phone) remoteFieldErrors.phone = errs.phone;
            if (errs.email) remoteFieldErrors.email = errs.email;
            if (errs.zip) remoteFieldErrors.zip = errs.zip;
            setRemoteErrors(remoteFieldErrors);
            // Route the user back to the EARLIEST step containing any of the
            // offending field ids — phone/email live on the contact step, but
            // zip/state live on the injury step earlier in the funnel, and
            // re-mounting the contact step would leave a zip-state error
            // invisible. Default to the current (submission) step when no
            // field map resolves.
            const errorFieldIds = Object.keys(remoteFieldErrors);
            let targetStepId = contactStepId;
            if (errorFieldIds.length > 0) {
              const candidateIdx = config.steps.findIndex((s) =>
                s.fields.some((f) => errorFieldIds.includes(f.id)),
              );
              if (candidateIdx >= 0) targetStepId = config.steps[candidateIdx]!.id;
            }
            // Re-mount the resolved step. Restore session progress so a refresh
            // mid-fix doesn't lose context.
            saveProgress(config.funnel_id, {
              session_id: sessionId,
              step_id: targetStepId,
              answers: useAnswers,
              history,
              updated_at: Date.now(),
            });
            setPhase("step");
            setStepId(targetStepId);
            return;
          }
          bus.emit("form_submit_failure", {
            step_id: stepId,
            tier: oc.tier_id,
            submit_latency_ms: submitLatencyMs,
            submit_error_code: String(result.status ?? result.error ?? "unknown"),
          });
        });
        return;
      }

      const nextHistory = [...history, stepId];
      setHistory(nextHistory);
      setErrors({});
      setPhase("step");
      setStepId(dest);
      saveProgress(config.funnel_id, {
        session_id: sessionId,
        step_id: dest,
        answers: useAnswers,
        history: nextHistory,
        updated_at: Date.now(),
      });
    },
    [bus, config, step, stepId, stepIndex, sessionId, history, fireStepPixels, fireResultPixels, submitLead],
  );

  const attemptContinue = useCallback(
    (useAnswers: Answers) => {
      const errs = validateStep(step.fields, useAnswers);
      if (Object.keys(errs).length > 0) {
        setErrors(errs);
        bus.emit("field_error", { step_id: stepId });
        return;
      }
      // A standing inline-validation failure (bad phone/email) blocks the
      // step — the error is already shown under the field.
      if (Object.keys(remoteErrors).length > 0) {
        bus.emit("field_error", { step_id: stepId });
        return;
      }
      const card = step.value_card;
      if (phase === "step" && card && (!card.when || matchGroup(card.when, useAnswers))) {
        setPhase("value_card");
        bus.emit("value_card_view", { step_id: stepId });
        return;
      }
      advance(useAnswers);
    },
    [advance, bus, phase, step, stepId, remoteErrors],
  );

  const onFieldChange = useCallback(
    (fieldId: string, value: FieldValue) => {
      const updated: Answers = { ...answers, [fieldId]: value as Answers[string] };
      setAnswers(updated);
      setErrors((e) => {
        if (!e[fieldId]) return e;
        const n = { ...e };
        delete n[fieldId];
        return n;
      });
      // Editing a field clears its standing inline-validation error — the
      // next blur re-checks the new value.
      setRemoteErrors((e) => {
        if (!e[fieldId]) return e;
        const n = { ...e };
        delete n[fieldId];
        return n;
      });
      bus.emit("field_change", { step_id: stepId, field_id: fieldId });

      const field = step.fields.find((f) => f.id === fieldId);
      const visible = step.fields.filter((f) => f.type !== "hidden");
      if (
        config.settings.auto_advance_single_select &&
        field?.type === "single_select" &&
        visible.length === 1
      ) {
        window.setTimeout(() => attemptContinue(updated), 200);
      }
    },
    [answers, bus, config, step, stepId, attemptContinue],
  );

  // ── inline lead-data validation on blur (Layer 1) ─────────────
  const onFieldBlur = useCallback(
    (fieldId: string) => {
      const field = step.fields.find((f) => f.id === fieldId);
      if (!field || (field.type !== "email" && field.type !== "tel")) return;
      if (!field.remote_validate) return;
      const value = answers[fieldId];
      if (typeof value !== "string" || value.trim() === "") return;
      if (lastValidated.current[fieldId] === value) return; // unchanged — skip
      lastValidated.current[fieldId] = value;

      const kind: "email" | "phone" = field.type === "email" ? "email" : "phone";
      fetch(validateUrl, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(kind === "email" ? { email: value } : { phone: value }),
      })
        .then((r) => r.json() as Promise<{ phone?: { valid?: boolean }; email?: { valid?: boolean } }>)
        .then((d) => {
          const verdict = kind === "email" ? d.email : d.phone;
          const hardFail = verdict != null && verdict.valid === false;
          setRemoteErrors((e) => {
            if (hardFail) {
              return {
                ...e,
                [fieldId]:
                  kind === "email"
                    ? "Please enter a valid, reachable email address."
                    : "That phone number looks invalid — please double-check it.",
              };
            }
            if (!e[fieldId]) return e;
            const n = { ...e };
            delete n[fieldId];
            return n;
          });
        })
        .catch(() => {
          // Fail-open — a network error never traps the user; /api/submit
          // re-validates server-side as the authoritative gate.
        });
    },
    [step, answers, validateUrl],
  );

  const onBack = useCallback(() => {
    // W1-C2: back_navigation event fires every time user hits Back, regardless
    // of whether the back navigation actually succeeds (e.g. on first step).
    // Provides drop-off analytics for "user retreated from step X".
    bus.emit("back_navigation", { step_id: stepId, step_index: stepIndex });
    if (phase === "value_card") {
      setPhase("step");
      return;
    }
    setHistory((h) => {
      if (h.length === 0) return h;
      const prev = h[h.length - 1]!;
      setStepId(prev);
      setPhase("step");
      setErrors({});
      return h.slice(0, -1);
    });
  }, [phase, bus, stepId, stepIndex]);

  // Browser back button → sync engine state to the URL the browser popped to.
  // Each virtualPage call pushed { funnelStep: stepId } onto the browser's
  // history state, so popstate.state.funnelStep tells us which step the URL
  // just popped to. Sync stepId directly (not via onBack) because the engine's
  // internal `history` stack may be empty after a localStorage restore — relying
  // on it would leave the page out of sync with the URL.
  useEffect(() => {
    const onPop = (e: PopStateEvent) => {
      const targetStepId = (e.state as { funnelStep?: string } | null)?.funnelStep;
      if (!targetStepId) return; // popped to a non-funnel entry; exit naturally
      if (targetStepId === stepIdRef.current) return;
      fromPopstate.current = true;
      setHistory((h) =>
        h.length > 0 && h[h.length - 1] === targetStepId ? h.slice(0, -1) : h,
      );
      setStepId(targetStepId);
      setPhase("step");
      setErrors({});
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // ── render ────────────────────────────────────────────────────
  const continueLabel = useMemo(() => {
    if (phase === "value_card") return "Continue";
    if (step.cta_label) return step.cta_label;
    return nextDestination(step, config.steps, answers) === RESULT
      ? "See my results"
      : "Continue";
  }, [phase, step, config.steps, answers]);

  const showBack =
    (config.settings.back_navigation && (history.length > 0 || phase === "value_card")) &&
    phase !== "result";

  const progressPct =
    config.settings.progress_bar && config.steps.length > 1
      ? Math.round(((stepIndex + (phase === "result" ? 1 : 0)) / config.steps.length) * 100)
      : null;

  return (
    <ProgressContext.Provider value={progressPct ?? 0}>
    <DynamicContext.Provider value={dynParams}>
    <div class="fe-shell">
      {isTestMode() ? (
        <div
          role="status"
          aria-live="polite"
          style="position:sticky;top:0;z-index:1000;margin:0 -16px 12px;padding:8px 16px;background:#FBBF24;color:#1F2937;font-weight:700;font-size:14px;text-align:center;letter-spacing:.04em;text-transform:uppercase;border-bottom:2px solid #B45309"
        >
          TEST MODE — submissions skip buyer notifications + route CAPI to Meta Test Events
        </div>
      ) : null}
      {config.theme.logo_url ? (
        <img class="fe-logo" src={config.theme.logo_url} alt={config.meta.name} />
      ) : config.theme.logo_text || config.theme.logo_mark_svg ? (
        <div class="fe-logo-mark" role="img" aria-label={config.meta.name}>
          {config.theme.logo_mark_svg ? (
            <span
              class="fe-logo-icon"
              aria-hidden="true"
              dangerouslySetInnerHTML={{ __html: config.theme.logo_mark_svg }}
            />
          ) : null}
          {config.theme.logo_text_parts && config.theme.logo_text_parts.length > 0 ? (
            <span class="fe-logo-words">
              <span class="fe-logo-text">
                {config.theme.logo_text_parts.map((part, i) => (
                  <span key={i} style={part.color ? `color:${part.color}` : undefined}>
                    {part.text}
                  </span>
                ))}
              </span>
              {config.theme.logo_tagline ? (
                <span class="fe-logo-tagline">{config.theme.logo_tagline}</span>
              ) : null}
            </span>
          ) : config.theme.logo_text ? (
            <span class="fe-logo-words">
              <span class="fe-logo-text">{config.theme.logo_text}</span>
              {config.theme.logo_tagline ? (
                <span class="fe-logo-tagline">{config.theme.logo_tagline}</span>
              ) : null}
            </span>
          ) : null}
        </div>
      ) : null}

      {phase === "result" && outcome ? (
        <ResultPage tier={config.result.tiers[outcome.tier_id]} outcome={outcome} />
      ) : phase === "value_card" && step.value_card ? (
        <div class="fe-fade" style="display:flex;flex-direction:column;gap:14px">
          <Blocks blocks={step.value_card.blocks} />
          <div class="fe-nav">
            <button type="button" class="fe-btn" onClick={() => advance(answers)}>
              {continueLabel}
            </button>
            {showBack ? (
              <button type="button" class="fe-btn fe-btn--ghost" onClick={onBack}>
                ← Back
              </button>
            ) : null}
          </div>
        </div>
      ) : (
        <StepRenderer
          step={step}
          answers={answers}
          errors={{ ...errors, ...remoteErrors }}
          continueLabel={continueLabel}
          showBack={showBack}
          onFieldChange={onFieldChange}
          onFieldBlur={onFieldBlur}
          onContinue={() => attemptContinue(answers)}
          onBack={onBack}
        />
      )}
    </div>
    </DynamicContext.Provider>
    </ProgressContext.Provider>
  );
}
